import {
  Component,
  OnInit,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  ProductService,
  ProductOption,
} from '@services/product.service';
import { Product, ProductCategory } from './type';
import { AuthService } from '@services/auth.service';
import { AnalyticsService } from '@services/analytics.service';
import { toTitleCase } from '@utils/title-case';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { WorkTypeSelectionModalComponent } from '../../../page/quote/modal/work-type-selection-modal.component';
import { LoginComponent } from '@auth/pages/login.component';
@Component({
  selector: 'app-products-section',
  templateUrl: './products-section.component.html',
  styleUrls: ['./products-section.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class ProductsSectionComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChildren('carousel') carousels!: QueryList<ElementRef>;

  categories: ProductCategory[] = [];
  loading = true;
  showProductModal = false;
  selectedProduct: Product | null = null;
  isApplicationOpen = true;
  isAdvantageOpen = true;
  showQuoteModal = false;

  // Tracking del scroll para los bullets
  private scrollPositions: number[] = [];
  private itemsPerView = 2; // Items que se mueven por bullet (2 productos)
  private itemWidth = 164; // ancho de tarjeta (156px) + gap (8px)
  private itemsPerSlide = 2; // Productos que se muestran por slide
  private scrollTimeouts: { [key: number]: any } = {}; // Para debounce del scroll
  private isScrolling: { [key: number]: boolean } = {}; // Track si está haciendo scroll

  // Propiedades calculadas para evitar ExpressionChangedAfterItHasBeenCheckedError
  protected bulletsData: Array<{
    bullets: number[];
    currentBullet: number;
    isPrevEnabled: boolean;
    isNextEnabled: boolean;
  }> = [];

  private auth = inject(AuthService);
  private bottomSheet = inject(MatBottomSheet);
  private analytics = inject(AnalyticsService);

  constructor(
    private router: Router,
    private productService: ProductService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.productService.clearCache();
    this.loadProducts();
  }

  /**
   * Actualiza las propiedades calculadas para una categoría específica
   */
  private updateBulletsData(categoryIndex: number): void {
    if (!this.categories[categoryIndex]) return;

    const category = this.categories[categoryIndex];
    const totalSlides = Math.ceil(category.products.length / this.itemsPerSlide);
    const bullets = Array.from({ length: totalSlides }, (_, i) => i);
    
    let currentBullet = 0;
    
    // Calcular bullet actual basado en scroll si está disponible
    const carouselArray = this.carousels?.toArray();
    if (carouselArray && carouselArray[categoryIndex]) {
      const carousel = carouselArray[categoryIndex].nativeElement;
      const scrollLeft = carousel.scrollLeft;
      const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;

      if (scrollLeft >= maxScrollLeft - 5) {
        currentBullet = totalSlides - 1;
      } else {
        const scrollProgress = scrollLeft / maxScrollLeft;
        currentBullet = Math.round(scrollProgress * (totalSlides - 1));
      }
    } else {
      // Fallback basado en currentIndex
      currentBullet = Math.floor(category.currentIndex / this.itemsPerSlide);
    }

    currentBullet = Math.max(0, Math.min(currentBullet, bullets.length - 1));

    // Asegurar que el array tenga el tamaño correcto
    while (this.bulletsData.length <= categoryIndex) {
      this.bulletsData.push({
        bullets: [],
        currentBullet: 0,
        isPrevEnabled: false,
        isNextEnabled: false
      });
    }

    // Actualizar datos
    this.bulletsData[categoryIndex] = {
      bullets,
      currentBullet,
      isPrevEnabled: currentBullet > 0,
      isNextEnabled: currentBullet < bullets.length - 1
    };
  }

  /**
   * Actualiza todas las propiedades calculadas
   */
  private updateAllBulletsData(): void {
    for (let i = 0; i < this.categories.length; i++) {
      this.updateBulletsData(i);
    }
  }

  loadProducts(): void {
    this.productService.getProductOptions().subscribe({
      next: (products: ProductOption[]) => {
        // Filtrar solo productos de Cemento y Concreto
        const cementoProducts = products
          .filter(
            (p) => p.category && p.category.toLowerCase().includes('cemento'),
          )
          .map((p, index) => ({
            id: `cemento-${index}`,
            category: p.category,
            name: p.label,
            imageUrl: p.imgUrl || 'assets/products/default.png',
            order: p.categoryOrder,
            brand: p.brand,
            measurementUnit: p.measurementUnit,
            mainUses: p.mainUses,
            advantages: p.advantages,
            usageType: p.usageType,
          }));

        const concretoProducts = products
          .filter(
            (p) => p.category && p.category.toLowerCase().includes('concreto'),
          )
          .map((p, index) => ({
            id: `concreto-${index}`,
            category: p.category,
            name: p.label,
            imageUrl: p.imgUrl || 'assets/products/default.png',
            order: p.categoryOrder,
            brand: p.brand,
            measurementUnit: p.measurementUnit,
            mainUses: p.mainUses,
            advantages: p.advantages,
            usageType: p.usageType,
          }));

        this.categories = [];

        // Agregar categoría Cemento si tiene productos
        if (cementoProducts.length > 0) {
          const sortedCemento = cementoProducts.sort(
            (a, b) => a.order - b.order,
          );
          this.categories.push({
            name: 'Productos UNACEM',
            currentIndex: 0,
            products: sortedCemento,
          });
        }

        // Agregar categoría Concreto si tiene productos
        if (concretoProducts.length > 0) {
          const sortedConcreto = concretoProducts.sort(
            (a, b) => a.order - b.order,
          );
          this.categories.push({
            name: 'Productos UNICON',
            currentIndex: 0,
            products: sortedConcreto,
          });
        }

        // Si no hay productos, cargar datos por defecto
        if (this.categories.length === 0) {
          console.warn(
            'No se encontraron productos de Cemento o Concreto. Cargando productos por defecto.',
          );
          this.loadDefaultProducts();
        }

        this.loading = false;
        
        // Inicializar propiedades calculadas después de cargar datos
        this.updateAllBulletsData();
      },
      error: (error) => {
        console.error('Error al cargar productos:', error);
        this.loading = false;
        // En caso de error, usar datos por defecto
        this.loadDefaultProducts();
        // Inicializar propiedades calculadas después de cargar datos por defecto
        this.updateAllBulletsData();
      },
    });
  }

  ngAfterViewInit(): void {
    // Configurar listeners de scroll después de que la vista esté lista
    setTimeout(() => {
      this.setupScrollListeners();
      this.initializeScrollPositions();
      // Inicializar propiedades calculadas después de que la vista esté lista
      this.updateAllBulletsData();
    }, 100);
  }

  private setupScrollListeners(): void {
    this.carousels.forEach((carouselRef, index) => {
      const carousel = carouselRef.nativeElement;

      // Listener para scroll continuo (incluye mouse wheel)
      carousel.addEventListener(
        'scroll',
        () => {
          this.isScrolling[index] = true;
          // Actualización inmediata sin debounce para bullets
          this.updateCurrentIndexFromScroll(index);

          // Debounce más corto solo para el snap final
          if (this.scrollTimeouts[index]) {
            clearTimeout(this.scrollTimeouts[index]);
          }

          this.scrollTimeouts[index] = setTimeout(() => {
            this.isScrolling[index] = false;
            this.snapToNearestSlide(index);
          }, 100); // Aumentado a 100ms para mayor estabilidad
        },
        { passive: true },
      );

      // Listener específico para wheel (mouse wheel horizontal)
      carousel.addEventListener(
        'wheel',
        (e: WheelEvent) => {
          // Si es scroll horizontal, actualizar inmediatamente
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            // Prevenir scroll vertical cuando hay scroll horizontal
            e.preventDefault();

            // Aplicar scroll horizontal manual
            carousel.scrollLeft += e.deltaX;

            // Actualizar inmediatamente sin delay
            this.updateCurrentIndexFromScroll(index);
          }
        },
        { passive: false },
      );

      // Listener para detectar fin de scroll en touch devices y scroll programático
      carousel.addEventListener(
        'scrollend',
        () => {
          if (this.scrollTimeouts[index]) {
            clearTimeout(this.scrollTimeouts[index]);
          }
          this.isScrolling[index] = false;
          this.snapToNearestSlide(index);
        },
        { passive: true },
      );

      // Listener adicional para touch events en móviles
      let touchStartX = 0;
      let touchEndX = 0;

      carousel.addEventListener(
        'touchstart',
        (e: TouchEvent) => {
          touchStartX = e.touches[0].clientX;
        },
        { passive: true },
      );

      carousel.addEventListener(
        'touchmove',
        () => {
          this.isScrolling[index] = true;
          // Actualización inmediata durante el touch scroll
          this.updateCurrentIndexFromScroll(index);
        },
        { passive: true },
      );

      carousel.addEventListener(
        'touchend',
        (e: TouchEvent) => {
          touchEndX = e.changedTouches[0].clientX;

          // Detectar dirección del swipe
          const swipeDistance = touchStartX - touchEndX;

          // Si hubo swipe significativo, actualizar después de un breve delay
          if (Math.abs(swipeDistance) > 30) {
            setTimeout(() => {
              this.isScrolling[index] = false;
              this.snapToNearestSlide(index);
            }, 100);
          } else {
            this.isScrolling[index] = false;
          }
        },
        { passive: true },
      );
    });
  }

  private initializeScrollPositions(): void {
    this.scrollPositions = new Array(this.categories.length).fill(0);
  }

  /**
   * Calcula dinámicamente el ancho de un slide basado en los elementos reales del DOM
   * Maneja correctamente el caso del último slide con menos productos
   */
  private calculateSlideWidth(
    categoryIndex: number,
    slideIndex?: number,
  ): number {
    const carouselArray = this.carousels.toArray();
    if (!carouselArray[categoryIndex]) {
      return this.itemWidth * this.itemsPerSlide; // Fallback al valor por defecto
    }

    const carousel = carouselArray[categoryIndex].nativeElement;
    const products = carousel.children;
    const category = this.categories[categoryIndex];

    if (products.length >= 2) {
      try {
        // Si es el último slide y tiene menos productos, calcular width específico
        const totalSlides = Math.ceil(
          category.products.length / this.itemsPerSlide,
        );
        const isLastSlide =
          slideIndex !== undefined && slideIndex === totalSlides - 1;
        const remainingProducts = category.products.length % this.itemsPerSlide;

        if (isLastSlide && remainingProducts > 0) {
          // Calcular ancho para el último slide con productos restantes
          const firstProduct = products[0] as HTMLElement;
          const firstRect = firstProduct.getBoundingClientRect();

          if (remainingProducts === 1) {
            // Solo 1 producto en el último slide
            return firstRect.width;
          } else {
            // Calcular ancho para productos restantes
            const secondProduct = products[1] as HTMLElement;
            const secondRect = secondProduct.getBoundingClientRect();
            const gap = secondRect.left - firstRect.right;
            return (
              firstRect.width * remainingProducts +
              gap * (remainingProducts - 1)
            );
          }
        }

        // Calcular ancho basado en los primeros 2 productos (un slide completo)
        const firstProduct = products[0] as HTMLElement;
        const secondProduct = products[1] as HTMLElement;

        // Calcular el ancho real incluyendo márgenes y gaps
        const firstRect = firstProduct.getBoundingClientRect();
        const secondRect = secondProduct.getBoundingClientRect();
        const gap = secondRect.left - firstRect.right;

        const calculatedWidth =
          firstRect.width * this.itemsPerSlide + gap * (this.itemsPerSlide - 1);

        // Validar que el cálculo sea razonable
        if (calculatedWidth > 0 && calculatedWidth < carousel.scrollWidth) {
          return calculatedWidth;
        }
      } catch (error) {
        console.warn('Error calculating slide width:', error);
      }
    }

    return this.itemWidth * this.itemsPerSlide; // Fallback
  }

  /**
   * Hace snap al slide más cercano después del scroll manual
   * Maneja correctamente el último slide con productos impares
   */
  private snapToNearestSlide(categoryIndex: number): void {
    const carouselArray = this.carousels.toArray();
    if (!carouselArray[categoryIndex]) return;

    const carousel = carouselArray[categoryIndex].nativeElement;
    const scrollLeft = carousel.scrollLeft;
    const category = this.categories[categoryIndex];
    const totalSlides = Math.ceil(
      category.products.length / this.itemsPerSlide,
    );
    const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;

    let targetSlideIndex: number;
    let targetScrollLeft: number;

    // Si estamos cerca del final, snap al último slide
    if (scrollLeft >= maxScrollLeft - 20) {
      targetSlideIndex = totalSlides - 1;
      targetScrollLeft = maxScrollLeft;
    } else {
      // Calcular slide más cercano basado en proporción
      const scrollProgress = scrollLeft / maxScrollLeft;
      targetSlideIndex = Math.round(scrollProgress * (totalSlides - 1));

      // Calcular posición del slide usando el ancho específico
      if (targetSlideIndex === totalSlides - 1) {
        targetScrollLeft = maxScrollLeft;
      } else {
        const slideWidth = this.calculateSlideWidth(
          categoryIndex,
          targetSlideIndex,
        );
        targetScrollLeft = targetSlideIndex * slideWidth;
      }
    }

    targetSlideIndex = Math.max(0, Math.min(targetSlideIndex, totalSlides - 1));

    // Si la posición actual no está alineada, hacer snap suave
    if (Math.abs(scrollLeft - targetScrollLeft) > 5) {
      carousel.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    }

    // Actualizar el índice si es necesario
    let newIndex = targetSlideIndex * this.itemsPerSlide;

    // Para el último slide con productos impares, mostrar los últimos 2 productos
    if (targetSlideIndex === totalSlides - 1) {
      const remainingProducts = category.products.length % this.itemsPerSlide;
      if (
        remainingProducts > 0 &&
        remainingProducts < this.itemsPerSlide &&
        category.products.length >= 2
      ) {
        // Para listas impares, mostrar los últimos 2 productos
        newIndex = Math.max(0, category.products.length - 2);
      } else if (remainingProducts > 0) {
        newIndex = category.products.length - remainingProducts;
      }
    }

    newIndex = Math.min(newIndex, category.products.length - 1);

    if (category.currentIndex !== newIndex) {
      category.currentIndex = newIndex;
      this.scrollPositions[categoryIndex] = targetScrollLeft;
      
      // Actualizar propiedades calculadas solo para esta categoría
      this.updateBulletsData(categoryIndex);
    }
  }

  updateCurrentIndexFromScroll(categoryIndex: number): void {
    const carouselArray = this.carousels.toArray();
    if (!carouselArray[categoryIndex] || !this.categories[categoryIndex])
      return;

    const carousel = carouselArray[categoryIndex].nativeElement;
    const scrollLeft = carousel.scrollLeft;
    const category = this.categories[categoryIndex];
    const totalSlides = Math.ceil(
      category.products.length / this.itemsPerSlide,
    );
    const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;

    let newIndex: number;

    // Si estamos al final del scroll, asegurarse de mostrar el último slide
    if (scrollLeft >= maxScrollLeft - 5) {
      // Para el último slide con listas impares, mostrar los últimos 2 productos
      const lastSlideIndex = totalSlides - 1;
      const remainingProducts = category.products.length % this.itemsPerSlide;

      if (
        remainingProducts > 0 &&
        remainingProducts < this.itemsPerSlide &&
        category.products.length >= 2
      ) {
        // Para listas impares, mostrar los últimos 2 productos
        newIndex = Math.max(0, category.products.length - 2);
      } else {
        // Para listas pares o casos especiales
        newIndex = lastSlideIndex * this.itemsPerSlide;
        if (remainingProducts > 0) {
          newIndex = category.products.length - remainingProducts;
        }
      }
    } else {
      // Calcular slide basado en proporción del scroll
      const scrollProgress = scrollLeft / maxScrollLeft;
      const slideIndex = Math.round(scrollProgress * (totalSlides - 1));
      const clampedSlideIndex = Math.max(
        0,
        Math.min(slideIndex, totalSlides - 1),
      );
      newIndex = clampedSlideIndex * this.itemsPerSlide;
    }

    // Asegurar que el índice no exceda el número de productos
    newIndex = Math.min(newIndex, category.products.length - 1);

    // Actualizar solo si hay cambios
    if (category.currentIndex !== newIndex) {
      category.currentIndex = newIndex;
      this.scrollPositions[categoryIndex] = scrollLeft;
      
      // Actualizar propiedades calculadas solo para esta categoría
      this.updateBulletsData(categoryIndex);
    }
  }

  loadDefaultProducts(): void {
    this.categories = [
      {
        name: 'Cemento',
        currentIndex: 0,
        products: [
          {
            id: '1',
            category: 'CEMENTO',
            name: 'Cemento Sol',
            imageUrl: 'assets/products/cemento-sol.png',
          },
          {
            id: '2',
            category: 'CEMENTO',
            name: 'Cemento APU',
            imageUrl: 'assets/products/cemento-apu.png',
          },
        ],
      },
      {
        name: 'Concreto',
        currentIndex: 0,
        products: [
          {
            id: '6',
            category: 'CONCRETO',
            name: 'Concreto Premezclado',
            imageUrl: 'assets/products/concreto-1.png',
          },
          {
            id: '7',
            category: 'CONCRETO',
            name: 'Concreto Estructural',
            imageUrl: 'assets/products/concreto-1.png',
          },
        ],
      },
    ];
  }

  scrollCarousel(categoryIndex: number, direction: 'prev' | 'next'): void {
    const category = this.categories[categoryIndex];
    const maxIndex = category.products.length - 2; // Mostramos 2 a la vez

    if (direction === 'prev' && category.currentIndex > 0) {
      category.currentIndex--;
    } else if (direction === 'next' && category.currentIndex < maxIndex) {
      category.currentIndex++;
    }
  }

  scrollCarouselHorizontal(
    categoryIndex: number,
    direction: 'prev' | 'next',
  ): void {
    const category = this.categories[categoryIndex];
    if (!category) return;

    const carouselArray = this.carousels.toArray();
    if (!carouselArray[categoryIndex]) return;

    const carousel = carouselArray[categoryIndex].nativeElement;

    // Marcar que estamos haciendo scroll programático para evitar interferencias
    this.isScrolling[categoryIndex] = true;

    // Limpiar timeout anterior si existe
    if (this.scrollTimeouts[categoryIndex]) {
      clearTimeout(this.scrollTimeouts[categoryIndex]);
    }

    // Calcular máximo índice basado en grupos de productos
    const totalSlides = Math.ceil(
      category.products.length / this.itemsPerSlide,
    );
    const maxSlideIndex = Math.max(0, totalSlides - 1);
    const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;

    // Calcular slide actual basado en la posición del scroll
    const currentScrollLeft = carousel.scrollLeft;
    let currentSlideIndex: number;

    if (currentScrollLeft >= maxScrollLeft - 5) {
      currentSlideIndex = maxSlideIndex;
    } else {
      const scrollProgress = currentScrollLeft / maxScrollLeft;
      currentSlideIndex = Math.round(scrollProgress * maxSlideIndex);
    }

    let newSlideIndex = currentSlideIndex;

    if (direction === 'next' && newSlideIndex < maxSlideIndex) {
      newSlideIndex++;
    } else if (direction === 'prev' && newSlideIndex > 0) {
      newSlideIndex--;
    } else {
      // No hay cambio, restaurar estado
      this.isScrolling[categoryIndex] = false;
      return;
    }

    // Calcular nuevo índice de producto
    let newIndex = newSlideIndex * this.itemsPerSlide;
    let targetScrollLeft: number;

    // Para el último slide con productos impares
    if (newSlideIndex === maxSlideIndex) {
      const remainingProducts = category.products.length % this.itemsPerSlide;
      if (
        remainingProducts > 0 &&
        remainingProducts < this.itemsPerSlide &&
        category.products.length >= 2
      ) {
        // Para listas impares, mostrar los últimos 2 productos
        newIndex = Math.max(0, category.products.length - 2);
      } else if (remainingProducts > 0) {
        newIndex = category.products.length - remainingProducts;
      }
      targetScrollLeft = maxScrollLeft;
    } else {
      // Usar cálculo dinámico consistente para slides normales
      const slideWidth = this.calculateSlideWidth(categoryIndex, newSlideIndex);
      targetScrollLeft = newSlideIndex * slideWidth;
    }

    // Asegurar que el índice no exceda el número de productos
    newIndex = Math.min(newIndex, category.products.length - 1);

    // Actualizar currentIndex inmediatamente
    category.currentIndex = newIndex;

    // Hacer scroll suave a la posición exacta del slide
    carousel.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });

    // Actualizar posición tracking
    this.scrollPositions[categoryIndex] = targetScrollLeft;
    
    // Actualizar propiedades calculadas para esta categoría
    this.updateBulletsData(categoryIndex);

    // Marcar fin de scroll programático después de la animación
    setTimeout(() => {
      this.isScrolling[categoryIndex] = false;
    }, 300);
  }

  getVisibleProducts(categoryIndex: number): Product[] {
    const category = this.categories[categoryIndex];
    return category.products.slice(
      category.currentIndex,
      category.currentIndex + 2,
    );
  }

  getBullets(categoryIndex: number): number[] {
    // Usar propiedades calculadas en lugar de calcular cada vez
    if (!this.bulletsData[categoryIndex]) {
      this.updateBulletsData(categoryIndex);
    }
    return this.bulletsData[categoryIndex]?.bullets || [];
  }

  getCurrentBullet(categoryIndex: number): number {
    // Usar propiedades calculadas en lugar de calcular cada vez
    if (!this.bulletsData[categoryIndex]) {
      this.updateBulletsData(categoryIndex);
    }
    return this.bulletsData[categoryIndex]?.currentBullet || 0;
  }

  /**
   * Método para hacer scroll directo a un bullet específico
   * Maneja correctamente el último bullet con productos impares
   */
  scrollToBullet(categoryIndex: number, bulletIndex: number): void {
    const category = this.categories[categoryIndex];
    if (!category) return;

    const carouselArray = this.carousels.toArray();
    if (!carouselArray[categoryIndex]) return;

    const carousel = carouselArray[categoryIndex].nativeElement;

    // Marcar que estamos haciendo scroll programático
    this.isScrolling[categoryIndex] = true;

    // Limpiar timeout anterior si existe
    if (this.scrollTimeouts[categoryIndex]) {
      clearTimeout(this.scrollTimeouts[categoryIndex]);
    }

    const totalSlides = Math.ceil(
      category.products.length / this.itemsPerSlide,
    );
    const targetSlideIndex = Math.max(
      0,
      Math.min(bulletIndex, totalSlides - 1),
    );
    const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;

    // Calcular índice del primer producto del slide y posición del scroll
    let targetProductIndex: number;
    let targetScrollLeft: number;

    if (targetSlideIndex === totalSlides - 1) {
      // Para el último slide, siempre mostrar 2 productos si es posible
      const remainingProducts = category.products.length % this.itemsPerSlide;
      if (
        remainingProducts > 0 &&
        remainingProducts < this.itemsPerSlide &&
        category.products.length >= 2
      ) {
        // Para listas impares, mostrar los últimos 2 productos
        targetProductIndex = Math.max(0, category.products.length - 2);
      } else {
        targetProductIndex = targetSlideIndex * this.itemsPerSlide;
      }
      targetScrollLeft = maxScrollLeft;
    } else {
      // Para slides normales
      targetProductIndex = targetSlideIndex * this.itemsPerSlide;
      const slideWidth = this.calculateSlideWidth(
        categoryIndex,
        targetSlideIndex,
      );
      targetScrollLeft = targetSlideIndex * slideWidth;
    }

    // Asegurar que el índice no exceda el número de productos
    targetProductIndex = Math.min(
      targetProductIndex,
      category.products.length - 1,
    );

    // Actualizar currentIndex
    category.currentIndex = targetProductIndex;

    // Hacer scroll a la posición
    carousel.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });

    this.scrollPositions[categoryIndex] = targetScrollLeft;
    
    // Actualizar propiedades calculadas para esta categoría
    this.updateBulletsData(categoryIndex);

    // Marcar fin de scroll programático después de la animación
    setTimeout(() => {
      this.isScrolling[categoryIndex] = false;
    }, 300);
  }

  onProductClick(product: Product): void {
    this.analytics.trackEvent('product_detail_opened', 'products_section', `${product.name} | ${product.brand}`);
    this.selectedProduct = product;
    this.showProductModal = true;
    this.isApplicationOpen = true;
  }

  closeProductModal(): void {
    this.showProductModal = false;
    this.selectedProduct = null;
  }

  toggleApplication(): void {
    this.isApplicationOpen = !this.isApplicationOpen;
  }

  toggleAdvantage(): void {
    this.isAdvantageOpen = !this.isAdvantageOpen;
  }

  openQuoteModal(): void {
    this.analytics.trackEvent('product_quote_started', 'products_section', this.selectedProduct?.name);
    this.showProductModal = false;

    const user = this.auth.user();

    if (!user) {
      this.showQuoteModal = true;
      return;
    }

    if (this.isUniconProduct()) {
      this.redirectToUniconChat(user);
      return;
    }

    this.bottomSheet.open(WorkTypeSelectionModalComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'custom-bottom-sheet-quote',
    });
  }

  private isUniconProduct(): boolean {
    return (
      this.selectedProduct?.brand?.toLowerCase().includes('unicon') ?? false
    );
  }

  private redirectToUniconChat(user: any): void {
    const { firstName, lastName } = this.extractUserNames(user);

    this.router.navigate(['/unicon-chat'], {
      queryParams: {
        firstName,
        lastName,
        userIdOnBusiness: user.id ?? 'guest',
      },
    });
  }

  private extractUserNames(user: any): { firstName: string; lastName: string } {
    if (user.firstname && user.lastname) {
      return {
        firstName: user.firstname,
        lastName: user.lastname,
      };
    }

    const fullName = user.name ?? '';
    const [firstName = 'Usuario', ...rest] = fullName.split(' ');
    const lastName = rest.join(' ') || 'Maestro';

    return { firstName, lastName };
  }

  closeQuoteModal(): void {
    this.showQuoteModal = false;
  }

  goToRegister(): void {
    this.closeQuoteModal();
    this.router.navigate(['/auth/register']);
  }

  goToLogin(): void {
    this.closeQuoteModal();
    this.bottomSheet.open(LoginComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'login-bottom-sheet',
    });
  }

  getProductSubtitle(productName: string): string {
    const lowerName = productName.toLowerCase();

    if (lowerName.includes('cemento') || lowerName.includes('sol')) {
      return 'Tipo I - Cemento Portland de uso general';
    } else if (lowerName.includes('concreto')) {
      return 'Concreto premezclado de alta resistencia';
    }

    return 'Producto de alta calidad';
  }

  getProductApplication(productName: Product) {
    //g('Obteniendo aplicación para el producto:', productName);
    // Aquí puedes personalizar el mensaje según el tipo de producto
    const applications: { [key: string]: string } = {
      cemento:
        'Construcciones en general y de gran envergadura cuando no se requieren características especiales o no especifique otro tipo de cemento.\n\nPreparación de concretos para cimientos, sobrecimientos, zapatas, columnas, vigas, losas aligeradas, losas macizas, muros de contención, pavimentos, veredas, tanques de agua, cámaras sépticas, etc.',
      concreto:
        'Perfecto para pisos, losas, muros y pavimentos. Ofrece resistencia y acabado profesional ideal para construcciones modernas.',
      default:
        'Producto de alta calidad para tu obra. Consulta con nuestros especialistas para más información sobre sus aplicaciones específicas.',
    };

    /*  const lowerName = productName.toLowerCase();
    
    if (lowerName.includes('cemento')) {
      return applications['cemento'];
    } else if (lowerName.includes('concreto')) {
      return applications['concreto'];
    }
    
    return applications['default']; */
  }

  /**
   * Utilidad para convertir texto a Title Case
   */
  toTitleCase(str: string): string {
    return toTitleCase(str);
  }

  ngOnDestroy(): void {
    // Limpiar todos los timeouts para evitar memory leaks
    Object.values(this.scrollTimeouts).forEach((timeout) => {
      if (timeout) {
        clearTimeout(timeout);
      }
    });
    this.scrollTimeouts = {};
  }
}
